import {Component, inject, Input, OnInit, ViewChild, ViewContainerRef} from '@angular/core';
import {ChartDatasetExtended} from "../base-measurement-chart";
import {DataType, MeterType, Organisation} from "@flowmaps-com/flowmaps-typescriptmodels";
import {ChartOptions, ChartOptionType, LocationRankingName, MeterTreeName} from "../../dashboard/dashboard.types";
import {DashboardContext} from "../../dashboard/dashboard.context";
import {lodash} from "../../../common/utils";
import {AppContext} from "../../../app-context";
import {Observable} from "rxjs";
import {PaginationComponent} from "../../../common/pagination/pagination.component";
import {ActiveElement, Chart, ChartEvent} from "chart.js";
import {Entity, EntityType} from "../../../handlers/entity";
import {TranslateDirective} from "../../../common/utils/translate.directive";
import {View} from "../../../common/view";
import {ChartModalOptions, ChartUtilsService} from "../chart-utils.service";
import {ChartDataPerMeasurement, ChartDataProvider} from "../../../utils/chart-data-provider";
import {Handler} from "../../../common/handler";
import {MiddleEllipsisPipe} from "../../../common/middle-ellipsis.pipe";
import {DataQueryFilters} from "../../measurements-component/measurements-handler.component";
import {performanceRecordsHaveData} from './performance-data-state';

@Component({
    template: "",
})
@Handler()
export class EntityPerformanceChartComponent extends View implements OnInit {
    chartUtils = inject(ChartUtilsService);
    _direction: 'ASC' | 'DESC' = 'DESC';
    weatherTypes: DataType[] = [];
    records: PerformanceRecord[] = [];
    perSquareMeterEnabled = false;
    compactTitle = false;
    currentPageRecords: PerformanceRecord[] = [];
    organisations: Organisation[] = [];
    locationRankingNameOptions: LocationRankingName[] = [];
    locationRankingNameFormatter = (value: LocationRankingName): string =>
        TranslateDirective.getTranslation(value, true) as string;
    locationRankingNameChanged = (_selectedName: LocationRankingName) => {};

    chartDataProvider: ChartDataProvider = new ChartDataProvider();
    options: ChartOptions;
    modalOptions: ChartModalOptions = {
        fullScreen: false,
    }
    selectedDataType: DataType
    filters: DataQueryFilters;
    entityType = (): EntityType => null;
    possibleDataTypes = (): DataType[] => null;
    createData = (result: ChartDataPerMeasurement): Observable<PerformanceRecord[]> => null;
    getAllEntities = (): Observable<Entity[]> => null;
    refresh = () => null;
    navigateToEntity: ((event: ChartEvent, elements: ActiveElement[], chart: Chart) => void) | boolean = false;
    onHover: ((event: ChartEvent, elements: ActiveElement[], chart: Chart) => void) | boolean = false;
    showChart: boolean = false;
    isLoading: boolean = true;
    stateMessage: string = 'No data available for the selected period';
    @ViewChild('container', {read: ViewContainerRef}) container: ViewContainerRef;
    @ViewChild("pagination") pagination: PaginationComponent<any>;

    @Input() dataType: DataType;
    @Input() showInReport: boolean;
    @Input() pageSize: number = 10;
    @Input() showBySquareMeter: boolean;

    // TODO: What to do with this, maybe generate an unique ID for each View
    private uniqueId = lodash.uniqueId();
    getInputId = (id: string) => `${this.uniqueId}-${id}`;

    ngOnInit() {
        this.sendQuery("getOrganisations").subscribe(o => this.organisations = o);
        this.subscribeTo("getChartOptions", ChartOptionType.Performance).subscribe((opts: ChartOptions) => {
            opts = lodash.merge({
                showAllDays: true,
                selectedDays: [],
                meterTreeView: {
                    selectedTreeKey: null,
                    selectedTagFilters: {},
                    selectedMeterName: MeterTreeName.Name,
                    showAsTable: false
                }
            }, opts || {});
            this.options = opts;
        });
    }

    get getSelectedDataType(): DataType {
        return this.selectedDataType
    }

    @Input()
    set data(modalOptions: ChartModalOptions) {
        this.modalOptions = modalOptions;
    }

    @Input()
    set sortingDirection(direction: 'ASC' | 'DESC') {
        this._direction = direction;
        this.refresh();
    }

    showBySquareMeterChange(showBySquareMeter: boolean) {
        this.options.showBySquareMeter = showBySquareMeter;
        this.refresh();
    }

    measurementTypes = (): DataType[] => {
        // Also exclude reactive feed-in when an older saved selection requests it.
        if ([DataType.electricityFeedInReactive, DataType.electricityFeedInReactiveOffPeak]
            .includes(this.getSelectedDataType)) return [];
        if (this.getSelectedDataType === DataType.electricityConsumption) {
            return [DataType.electricityConsumption, DataType.electricityConsumptionOffPeak];
        }
        if (this.getSelectedDataType === DataType.electricityFeedIn) {
            return [DataType.electricityFeedIn, DataType.electricityFeedInOffPeak];
        }
        if (this.getSelectedDataType === DataType.electricityConsumptionReactive) {
            return [DataType.electricityConsumptionReactive, DataType.electricityConsumptionReactiveOffPeak];
        }
        return [this.getSelectedDataType];
    };

    measurementUnit = (): string => `${DashboardContext.getMeasurementUnit(this.getSelectedDataType)}${this.showPerSquareMeter() ? '/m²' : ''}`;

    measurementTypeChanged = (m: DataType) => {
        this.selectedDataType = m;
        this.refresh();
    }

    setData(result: ChartDataPerMeasurement) {
        this.createData(result).subscribe({
            next: data => {
                let dataSorted = lodash.sortBy(data, a => {
                    const value = a.value ? a.value : 0;
                    const estimatedValue = a.estimatedValue ? a.estimatedValue : 0;
                    return value + estimatedValue;
                });
                if (this._direction === "DESC") {
                    dataSorted = dataSorted.reverse();
                }
                this.records = dataSorted;
                this.showChart = performanceRecordsHaveData(dataSorted);
                this.isLoading = false;
                this.applyPaging(dataSorted.slice(0, this.pageSize));
            },
            error: this.showLoadError
        });
    }

    startLoading = () => {
        this.isLoading = true;
        this.showChart = false;
        this.stateMessage = 'No data available for the selected period';
    }

    showNoData = () => {
        this.records = [];
        this.showChart = false;
        this.isLoading = false;
        this.stateMessage = 'No data available for the selected period';
        this.applyPaging([]);
    }

    showLoadError = () => {
        this.records = [];
        this.showChart = false;
        this.isLoading = false;
        this.stateMessage = 'Unable to load data';
        this.applyPaging([]);
    }

    applyPaging = (items: PerformanceRecord[]) => {
        this.currentPageRecords = items;
        const estimatedData = items.map(a => a.estimatedValue);
        this.chartDataProvider.emit({
            labels: items.map(d => MiddleEllipsisPipe.format(d.label, 15)),
            datasets: [{
                entityId: null,
                measurementType: this.getSelectedDataType,
                dataset: this.getDataset(false, items.map(a => a.value))
            }].concat(estimatedData.some(a => a > 0) ? [{
                entityId: null,
                measurementType: this.getSelectedDataType,
                dataset: this.getDataset(true, estimatedData)
            }] : [])
        });
    }

    private getDataset = (estimated: boolean, data: number[]): ChartDatasetExtended => {
        const color = AppContext.getChartColorForMeasurementAsString(this.getSelectedDataType);
        const label = this.measurementName(this.getSelectedDataType);
        return {
            label: estimated ? `${label} (${TranslateDirective.getTranslation("estimated", true)})` : label,
            data: data,
            backgroundColor: estimated ? "#FFFFFF" : color,
            borderColor: color,
            borderWidth: estimated ? 1 : 0,
            measurementType: this.getSelectedDataType,
            tooltip: {
                formatter: this.chartUtils.getCustomTooltipFormatter(this.getSelectedDataType, this.showPerSquareMeter()),
                labelOverride: this.measurementName(this.getSelectedDataType),
                data: data,
                estimated: estimated
            }
        }
    }

    dropdownFormatter = (measurementType: DataType) => this.measurementName(measurementType, false);

    measurementName = (measurementType: DataType, translate: boolean = true): string => {
        return AppContext.entityPerformanceMeasurementName(measurementType, translate);
    }

    showPerSquareMeter = (): boolean => {
        const showBySquareMeter = this.showBySquareMeter !== undefined ? this.showBySquareMeter : this.options.showBySquareMeter;
        return this.perSquareMeterEnabled && showBySquareMeter;
    }
}

export interface PerformanceRecord {
    entityId: string;
    value: number;
    estimatedValue: number;
    label: string;
    parentId?: string;
    childIds?: string[];
    virtual?: boolean;
    type?: MeterType;
    measurementType?: DataType;
}
